import type { InventoryCoveragePolicy } from '@/features/decisions/decisions.api.types';
import type { ForecastQuantiles } from '@/features/decisions/utils/inventoryCoverageChartOption';
import { addDaysToIsoDate } from '@/utils/date';

// Per-week stockout risk for the previewed policy.
//
// The forecast quantiles are CUMULATIVE demand from the decision date over
// weekly spans. Stockout risk by week t is the chance cumulative demand
// outruns the inventory available by then:
//
//   risk(t) = P( cumulative_demand(t) > supply(t) ) = 1 − F_t( supply(t) )
//
// where supply(t) = on-hand + in-flight, stepping up by the reorder once it
// lands at the lead-time receipt. F_t is read by INTERPOLATING the empirical
// forecast quantile grid directly — the same representation the backend's
// QuantileDemandStrategy uses to size the reorder — so the chart reconciles with
// the recommendation instead of imposing a parametric (lognormal) shape.
//
// Limitation: the grid tops out at the highest served quantile (e.g. 0.98), so
// in-stock probability caps there and stockout risk floors at (1 − p_max).
//
// Source priority: a backend per-week stockout-probability series is used
// directly when present; otherwise we derive it client-side from the grid.
// Both paths return the same WeekRisk shape, so swapping is a no-op upstream.

export interface WeekRisk {
  period: string; // ISO week-ending date
  supply: number; // inventory available by this week (incl. reorder once landed)
  inStockProb: number; // P(no stockout by this week), 0..1
  stockoutRisk: number; // 1 − inStockProb, 0..1
  expectedUnitsShort: number; // E[(demand − supply)+] — fill-rate-side magnitude
  tier: RiskTier;
  reorderLanded: boolean; // has the reorder receipt arrived by this week?
  source: 'backend' | 'estimated';
}

export type RiskTier = 'ok' | 'watch' | 'risk';

export const TIER_COLOR: Record<RiskTier, string> = {
  ok: '#16a34a', // green-600
  watch: '#eab308', // yellow-500
  risk: '#dc2626', // red-600
};

export const TIER_BG: Record<RiskTier, string> = {
  ok: '#dcfce7', // green-100
  watch: '#fef9c3', // yellow-100
  risk: '#fee2e2', // red-100
};

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

// One (probability, cumulative-demand value) point on the empirical CDF.
type QPoint = { p: number; v: number };

// Build the strictly-increasing CDF points for one week (column i) from the
// quantile grid. Drops nulls/negatives and any non-increasing values so the CDF
// is monotonic. Needs ≥2 points to interpolate.
function weekPoints(grid: ForecastQuantiles['grid'], i: number): QPoint[] {
  if (!grid) return [];
  const raw = grid
    .map((level) => ({ p: level.p, v: level.values[i] }))
    .filter((pt): pt is QPoint => pt.v != null && pt.v >= 0)
    .sort((a, b) => a.v - b.v || a.p - b.p);
  const mono: QPoint[] = [];
  for (const pt of raw) {
    if (mono.length === 0 || pt.v > mono[mono.length - 1].v) mono.push(pt);
  }
  return mono;
}

// In-stock probability F(S) = P(cumulative demand ≤ supply), by interpolating the
// empirical quantile grid (linear in value-space). Below the lowest quantile we
// interpolate from the origin; above the highest we cap at p_max (the grid can't
// assert a higher service level).
function inStockFromGrid(pts: QPoint[], supply: number): number | null {
  if (pts.length < 2) return null;
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (supply <= 0) return 0;
  if (supply <= first.v) return first.v > 0 ? clamp01(first.p * (supply / first.v)) : first.p;
  if (supply >= last.v) return last.p;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (supply <= b.v) return clamp01(a.p + ((b.p - a.p) * (supply - a.v)) / (b.v - a.v));
  }
  return last.p;
}

// Expected units short = E[(demand − supply)+] = ∫_S^∞ (1 − F(d)) dd, approximated
// by the trapezoid over the grid points above supply. Truncates at the top
// quantile, so the extreme tail (beyond p_max) is not counted — a slight
// underestimate for heavy-tailed demand.
function expectedShortfallFromGrid(pts: QPoint[], supply: number): number {
  if (pts.length < 2) return 0;
  let area = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (b.v <= supply) continue;
    const lo = Math.max(supply, a.v);
    const fLo = lo === a.v ? a.p : a.p + ((b.p - a.p) * (lo - a.v)) / (b.v - a.v);
    area += ((b.v - lo) * (1 - fLo + (1 - b.p))) / 2;
  }
  return Math.max(0, area);
}

export function targetRiskFor(policy: InventoryCoveragePolicy | undefined): number {
  const sl = policy?.model_target_service_level ?? 0.95;
  return clamp01(1 - sl);
}

// Tiers keyed to the policy's service target: green within target, amber up to
// 3× the target risk (floored at 10% so a 98% target still shows a usable
// amber band), red beyond.
export function riskTier(risk: number, targetRisk: number): RiskTier {
  const watchCeiling = Math.max(0.1, 3 * targetRisk);
  if (risk <= targetRisk) return 'ok';
  if (risk <= watchCeiling) return 'watch';
  return 'risk';
}

export function stockoutRiskByWeek(
  policy: InventoryCoveragePolicy | undefined,
  decisionDate: string,
  quantiles: ForecastQuantiles | undefined,
  // Optional what-if: override the reorder quantity. Demand is independent of
  // order size, so post-receipt supply simply shifts by (override − recommended)
  // — the review-cycle risk responds, the lead-time weeks do not.
  overrideReorder?: number | null,
): WeekRisk[] {
  if (!policy || !quantiles || quantiles.timestamps.length === 0) return [];

  // Supply has two regimes over the protection period:
  //   • pre-receipt (during lead time): only on-hand covers demand.
  //   • post-receipt: full inventory position = on-hand + in-flight, which is the
  //     optimizer's model_target_inv_pos.
  // The reorder is ALREADY part of ending_inflight — do NOT add
  // model_order_constrained again. Verified against sim.results: ending_inflight
  // == order_constrained and target_inv_pos == beginning_available + ending_inflight.
  const available = policy.model_beginning_available ?? 0;
  const inflight = policy.model_ending_inflight ?? 0;
  const recommendedReorder = policy.model_order_constrained ?? 0;
  const reorderDelta = overrideReorder != null ? overrideReorder - recommendedReorder : 0;
  const postReceiptSupply = (policy.model_target_inv_pos ?? available + inflight) + reorderDelta;

  // Reorder lands at the lead-time receipt (decision date + total lead time).
  const leadWeeks = policy.total_lead_time ?? 0;
  const receiptDate = addDaysToIsoDate(decisionDate, leadWeeks * 7);

  // Scope: the protection period the reorder is accountable for = lead time +
  // periods between replenishment (decision date → next-order arrival). The
  // service-level target only applies here; weeks past it belong to the next
  // order, so we stop the strip at the protection-period end (when defined).
  const pbrWeeks = policy.periods_between_replenishment ?? 0;
  const protectionWeeks = leadWeeks + pbrWeeks;
  const protectionEnd = protectionWeeks > 0 ? addDaysToIsoDate(decisionDate, protectionWeeks * 7) : null;

  const targetRisk = targetRiskFor(policy);
  const backend = new Map((policy.model_stockout_prob_over_lt_ph ?? []).map((p) => [p.period, p.value]));

  const out: WeekRisk[] = [];
  quantiles.timestamps.forEach((ts, i) => {
    const period = addDaysToIsoDate(ts, quantiles.spans[i]);
    if (protectionEnd && period > protectionEnd) return; // beyond this reorder's protection
    const reorderLanded = period >= receiptDate;
    const supply = reorderLanded ? postReceiptSupply : available;

    // Empirical CDF for this week from the quantile grid — drives the expected
    // shortfall, and the probability too unless the backend supplies it.
    const pts = weekPoints(quantiles.grid, i);
    const backendRisk = backend.get(period);
    let stockoutRisk: number;
    let source: WeekRisk['source'];
    if (backendRisk != null) {
      stockoutRisk = clamp01(backendRisk);
      source = 'backend';
    } else {
      const inStock = inStockFromGrid(pts, supply);
      if (inStock == null) return; // not enough grid points to estimate this week
      stockoutRisk = 1 - inStock;
      source = 'estimated';
    }
    const expectedUnitsShort = pts.length >= 2 ? expectedShortfallFromGrid(pts, supply) : 0;

    out.push({
      period,
      supply,
      inStockProb: 1 - stockoutRisk,
      stockoutRisk,
      expectedUnitsShort,
      tier: riskTier(stockoutRisk, targetRisk),
      reorderLanded,
      source,
    });
  });

  return out;
}

// Headline summary: peak risk week and whether the plan holds target all weeks.
export function summarizeRisk(weeks: WeekRisk[], targetRisk: number) {
  if (weeks.length === 0) return null;
  const peak = weeks.reduce((a, b) => (b.stockoutRisk > a.stockoutRisk ? b : a));
  const firstBreach = weeks.find((w) => w.stockoutRisk > targetRisk) ?? null;
  return { peak, firstBreach, holdsTarget: !firstBreach };
}
